/**
 * Canonical document <-> metric <-> valuation-method graph.
 *
 * Everything is derived from a single source of truth, `METHOD_REQUIREMENTS`,
 * which declares, per valuation method, the metrics it needs and the document
 * types that contain each metric (`satisfiedByDocTypes`). This module inverts
 * that declaration so callers can reliably ask:
 *
 *   - "Which metrics does TAX_RETURN provide?"          -> getMetricsProvidedByDocTypes
 *   - "Which valuation methods need REVENUE?"           -> getMethodsRequiringMetric
 *   - "Which methods does this document help unlock?"   -> getMethodsForDocTypes
 *
 * The point: the reason we ask for a document is never hand-authored — it is a
 * traversal of this graph (document -> metrics -> valuations), so the request,
 * the explanation, and the "what's blocking this valuation" check all stay in
 * lockstep with the models that actually run.
 */

import {
  ALL_VALUATION_METHODS,
  METHOD_REQUIREMENTS,
  type ValuationMethod,
} from "./method-requirements";

export interface MetricProvision {
  metric: string;
  /** Friendly label, e.g. "revenue", "SDE". */
  label: string;
  /** Human description from the underlying metric requirement. */
  description: string;
  /** Methods that require this metric (and list one of the doc types as a source). */
  methods: ValuationMethod[];
  /** "required" if any requiring method treats it as required, else "recommended". */
  importance: "required" | "recommended";
}

/** Friendly, human-readable labels for known metric keys. Falls back to a
 *  lower-cased, space-separated version of the key. */
const METRIC_LABELS: Record<string, string> = {
  REVENUE: "revenue",
  SDE: "SDE",
  OWNER_COMPENSATION: "owner compensation",
  EARNINGS_BEFORE_TAX: "earnings before tax",
  DEPRECIATION: "depreciation",
  INTEREST_EXPENSE: "interest expense",
  INCOME_TAX: "income tax",
  CURRENT_ASSETS: "current assets",
  CURRENT_LIABILITIES: "current liabilities",
  TOTAL_ASSETS: "total assets",
  TOTAL_LIABILITIES: "total liabilities",
  INVENTORY: "inventory",
  PPE_NET: "net property, plant & equipment",
};

export function formatMetricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric.replaceAll("_", " ").toLowerCase();
}

// docType -> (metric -> provision), built once from METHOD_REQUIREMENTS.
const DOC_TYPE_INDEX: Map<string, Map<string, MetricProvision>> = (() => {
  const index = new Map<string, Map<string, MetricProvision>>();
  for (const method of ALL_VALUATION_METHODS) {
    const req = METHOD_REQUIREMENTS[method];
    for (const metric of req.requiredMetrics) {
      for (const docType of metric.satisfiedByDocTypes) {
        let byMetric = index.get(docType);
        if (!byMetric) {
          byMetric = new Map();
          index.set(docType, byMetric);
        }
        const existing = byMetric.get(metric.metric);
        if (existing) {
          if (!existing.methods.includes(method)) existing.methods.push(method);
          if (metric.importance === "required")
            existing.importance = "required";
        } else {
          byMetric.set(metric.metric, {
            metric: metric.metric,
            label: formatMetricLabel(metric.metric),
            description: metric.description,
            methods: [method],
            importance: metric.importance,
          });
        }
      }
    }
  }
  return index;
})();

/** Metrics that the given document types can provide, merged across types and
 *  ordered required-first. */
export function getMetricsProvidedByDocTypes(
  docTypes: string[],
): MetricProvision[] {
  const merged = new Map<string, MetricProvision>();
  for (const docType of docTypes) {
    const byMetric = DOC_TYPE_INDEX.get(docType);
    if (!byMetric) continue;
    for (const [metricKey, provision] of byMetric) {
      const existing = merged.get(metricKey);
      if (existing) {
        for (const method of provision.methods) {
          if (!existing.methods.includes(method)) existing.methods.push(method);
        }
        if (provision.importance === "required")
          existing.importance = "required";
      } else {
        merged.set(metricKey, {
          ...provision,
          methods: [...provision.methods],
        });
      }
    }
  }
  return [...merged.values()].sort((a, b) => {
    if (a.importance === b.importance) return 0;
    return a.importance === "required" ? -1 : 1;
  });
}

/** Valuation methods that require a metric provided by the given document
 *  types, in canonical method order. */
export function getMethodsForDocTypes(docTypes: string[]): ValuationMethod[] {
  const methods = new Set<ValuationMethod>();
  for (const provision of getMetricsProvidedByDocTypes(docTypes)) {
    for (const method of provision.methods) methods.add(method);
  }
  return ALL_VALUATION_METHODS.filter((method) => methods.has(method));
}

/** Methods that require the given metric. */
export function getMethodsRequiringMetric(metric: string): ValuationMethod[] {
  return ALL_VALUATION_METHODS.filter((method) =>
    METHOD_REQUIREMENTS[method].requiredMetrics.some(
      (m) => m.metric === metric,
    ),
  );
}

export function getMethodLabel(method: ValuationMethod): string {
  return METHOD_REQUIREMENTS[method].label;
}
